import React, { useMemo } from 'react';
import { useSelector } from 'react-redux';
import { renderParagraphs } from '../../Utils/Content/content-section-component';
import VaultDome from '../../Components/vault-dome-component';

export default function PricingSection({ pricingData }) {
    const systemData = useSelector((state) => state.systemData || {});
    const { dir, businessInfo = {} } = systemData;

    const data = useMemo(() => {
        if (!pricingData) return null;
        return {
            title: pricingData.title,
            items: pricingData.items || [],
            text: pricingData.guaranteeText || pricingData.content, 
        };
    }, [pricingData]);

    if (!data || data.items.length === 0) return null;

    return (
        <VaultDome height={100} offset={20}>
            <section className="relative w-full text-white font-assistant overflow-hidden pb-20 md:pb-32">
                
                {/* Background Branding Accent */}
                <div className="absolute top-0 right-0 w-full md:w-[50%] h-full opacity-5 md:opacity-10 pointer-events-none select-none overflow-hidden">
                    <h2 className="text-[10rem] md:text-[20rem] font-black uppercase leading-none translate-x-10 md:translate-x-20 er">
                        {businessInfo.businessName?.split(' ')[0]}
                    </h2>
                </div>

                <div className="container mx-auto px-6 max-w-7xl relative z-20 pt-10">
                    
                    {/* 1. Header Block */}
                    <div className="flex flex-col items-center text-center mb-16 md:mb-24">
                        <h2 className="text-4xl md:text-8xl font-black leading-[0.9] uppercase er mb-6 md:mb-10">
                            {data.title}
                        </h2>
                        
                        {data.text && (
                            <div className="max-w-2xl text-base md:text-xl font-medium text-white/80 leading-relaxed px-4">
                                {renderParagraphs(data.text)}
                            </div>
                        )}
                    </div>

                    {/* 2. The Ledger */}
                    <div className="w-full border-t border-white/20">
                        {data.items.map((item, idx) => (
                            <div 
                                key={idx} 
                                className={`
                                    group flex flex-col md:flex-row md:items-center justify-between 
                                    py-10 md:py-16 border-b border-white/10 
                                    sm:hover:bg-white/5 transition-all duration-500 
                                    ${dir === 'rtl' ? 'md:flex-row-reverse text-right' : 'text-left'}
                                `}
                            >
                                {/* Service Name Area */}
                                <div className="flex-1 space-y-2 md:space-y-3 mb-6 md:mb-0 pr-4">
                                    <h3 className="text-2xl md:text-5xl lg:text-6xl font-black uppercase er group-hover:translate-x-2 transition-transform duration-500 break-words">
                                        {item.service}
                                    </h3>
                                    <div className={`flex items-center gap-2 md:gap-3 ${dir === 'rtl' ? 'flex-row-reverse' : ''}`}>
                                        <span className="shrink-0 w-1.5 h-1.5 md:w-2 md:h-2 rounded-full bg-secondary-500 shadow-[0_0_10px_rgba(245,158,11,0.5)]" />
                                        <p className="text-white/40 font-bold uppercase text-[9px] md:text-[10px] tracking-[0.2em]">
                                            {item.note}
                                        </p>
                                    </div>
                                </div>

                                {/* Price & Arrow Area */}
                                <div className={`flex items-center justify-between md:justify-end gap-6 md:gap-12 shrink-0`}>
                                    <div className={`${dir === 'rtl' ? 'md:text-left text-right' : 'md:text-right text-left'}`}>
                                        {/* <span className="block text-[9px] md:text-[10px] font-black text-white/30 uppercase tracking-widest mb-1 md:mb-2">
                                            Est. Investment
                                        </span> */}
                                        <div className="text-3xl md:text-6xl lg:text-8xl font-black er leading-none whitespace-nowrap">
                                            {item.range}
                                        </div>
                                    </div>
                                    
                                    {/* Fixed-size Arrow Container to prevent squashing */}
                                    <div className="hidden md:flex items-center justify-center w-12 h-12 lg:w-16 lg:h-16 shrink-0 rounded-full border border-white/20 group-hover:bg-white group-hover:text-[#0772BF] transition-all duration-500 group-hover:rotate-45">
                                        <svg className="w-5 h-5 lg:w-6 lg:h-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3} d="M14 5l7 7m0 0l-7 7m7-7H3" />
                                        </svg>
                                    </div>
                                </div>
                            </div>
                        ))}
                    </div>

                </div>
            </section>
        </VaultDome>
    );
}